import { Event } from "@mui/icons-material";
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment-timezone";
import { useCallback, useEffect, useRef, useState } from "react";

import { useVerification } from "../../../../../context/Verification.context";
import { ArtifactStyle } from "./VerficationArtifactsStyles";
import { weekDays } from "../../../../../utils/Utils";
import TextUnAvailable from "./TextUnAvailable";
import Theme from "../../../../../utils/Theme";

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Calendar color scheme: lighter green fill with dark text for the
// selected date (for contrast), and the theme's primary green for all
// other calendar letters/numbers — day numbers, weekday captions, and
// the month/year header label.
const CALENDAR_SELECTED_BG = "#CFF3DC"; // light green
const CALENDAR_SELECTED_BG_HOVER = "#BEEDD0"; // slightly deeper on hover/focus
const CALENDAR_DARK_TEXT = "#14432B"; // dark green-black, kept on the
// selected day specifically — Theme.palette.primary.main ("#00ab55")
// text on the CALENDAR_SELECTED_BG light-green fill would be too low
// contrast, undoing the readability fix.

// Shared sx applied to the calendar's Paper (both the mobile Dialog and
// the desktop Popper use this) so the color scheme stays consistent
// regardless of which variant MUI renders.
const calendarColorSx = {
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: CALENDAR_SELECTED_BG,
    color: CALENDAR_DARK_TEXT,
    fontWeight: 600,
    "&:hover, &:focus": {
      backgroundColor: CALENDAR_SELECTED_BG_HOVER,
    },
  },
  "& .MuiPickersDay-root": {
    color: Theme.palette.primary.main,
  },
  "& .MuiPickersDay-today": {
    borderColor: Theme.palette.primary.main,
  },
  "& .MuiPickersCalendarHeader-label": {
    color: Theme.palette.primary.main,
  },
  "& .MuiTypography-caption": {
    color: Theme.palette.primary.main,
  },
  // The Mobile Dialog variant's toolbar header — "SELECT DATE" label and
  // the large "Fri, Aug 28" date title above the calendar grid. Only
  // present in Dialog mode (mobile); harmless no-op on the desktop
  // Popper, which has no toolbar.
  "& .MuiPickersToolbar-root .MuiTypography-root": {
    color: Theme.palette.primary.main,
  },
  "& .MuiPickersToolbarText-root": {
    color: Theme.palette.primary.main,
  },
  // Edit pencil icon (toggles the Mobile Dialog toolbar between the
  // text-input view and the calendar view).
  "& .MuiPickersToolbar-root .MuiIconButton-root": {
    color: Theme.palette.primary.main,
  },
  "& .MuiPickersToolbar-root .MuiIconButton-root .MuiSvgIcon-root": {
    color: Theme.palette.primary.main,
  },
  // Prev / next month chevrons in the calendar header. MUI's own
  // .Mui-disabled opacity still applies on top of this, so a disabled
  // chevron (e.g. "prev" when the earliest selectable month is showing)
  // stays visibly greyed-out/inactive rather than solid green.
  "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
    color: Theme.palette.primary.main,
  },
  "& .MuiPickersArrowSwitcher-root .MuiSvgIcon-root": {
    color: Theme.palette.primary.main,
  },
  // The dropdown caret next to "August 2026" that flips the calendar
  // into the year-list view.
  "& .MuiPickersCalendarHeader-switchViewButton": {
    color: Theme.palette.primary.main,
  },
  "& .MuiPickersCalendarHeader-switchViewIcon": {
    color: Theme.palette.primary.main,
  },
  // The year-list view itself (shown after tapping the caret above).
  // Unselected years: primary green text, no fill. Selected year:
  // same light-green-fill / dark-text treatment as a selected day,
  // for the same contrast reason.
  "& .PrivatePickersYear-yearButton": {
    color: Theme.palette.primary.main,
  },
  "& .PrivatePickersYear-yearButton.Mui-selected": {
    backgroundColor: CALENDAR_SELECTED_BG,
    color: CALENDAR_DARK_TEXT,
    fontWeight: 600,
  },
  // Fallback selectors — some @mui/x-date-pickers v5 patch versions use
  // MuiYearPicker-root/button instead of the PrivatePickersYear- classes
  // above; harmless no-ops if unused.
  "& .MuiYearPicker-root button": {
    color: Theme.palette.primary.main,
  },
  "& .MuiYearPicker-root button.Mui-selected": {
    backgroundColor: CALENDAR_SELECTED_BG,
    color: CALENDAR_DARK_TEXT,
    fontWeight: 600,
  },
};

// ── Touch-friendly sizing for the Time / Preference selects ───────────
// Mobile only. 56px meets the common 48px+ touch-target guidance and is
// easier for users with limited dexterity. Desktop is left unchanged.
const touchSelectSx = (isMobile) =>
  isMobile
    ? {
        "& .MuiSelect-select.MuiSelect-select": {
          minHeight: "56px",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          fontSize: "18px",
          paddingRight: "48px",
        },
        "& .MuiSelect-icon": { fontSize: "2.2rem", right: "10px" },
      }
    : {};

// Same touch sizing for the Date input box (the field that opens the
// calendar). Only the input box is enlarged — the calendar popup itself
// is not affected.
const touchDateInputSx = (isMobile) =>
  isMobile
    ? {
        "& .MuiInputBase-root": { minHeight: "56px" },
        "& .MuiInputBase-input": {
          height: "56px",
          boxSizing: "border-box",
          fontSize: "18px",
          padding: "0 14px",
        },
        "& .MuiInputAdornment-root .MuiSvgIcon-root": { fontSize: "2.2rem" },
      }
    : {};

const touchMenuItemSx = (isMobile) =>
  isMobile
    ? {
        minHeight: "56px",
        fontSize: "18px",
        whiteSpace: "normal", // long option text wraps instead of clipping
        margin: "6px", // gap between options to avoid mis-taps
      }
    : {};

const AppointmentResponse = ({
  artifact,
  setAppointmentScreen,
  isAIAppointment = false,
  onInteraction,
}) => {
  const verification = useVerification();
  // Reactive mobile check (updates on resize/rotation), matches the
  // existing 750px breakpoint used elsewhere in this component.
  const isMobile = useMediaQuery("(max-width:750px)");

  // ── Calendar "fill the 9:16 layout" sizing ──────────────────────────
  // On touch devices, @mui/x-date-pickers v5 renders DatePicker as a
  // MobileDatePicker internally (Dialog, not Popper) — that's a design
  // requirement here (centered, so it can grow without overflowing an
  // anchored dropdown position). We measure the calendar's natural
  // (1x) size once, then compute the largest scale factor that still
  // fits inside the real available viewport, recomputed on resize/
  // rotation so it always fills as much of the frame as possible.
  const calendarPaperRef = useRef(null);
  const calendarNaturalSize = useRef(null); // cached {width, height} at 1x
  const [calendarScale, setCalendarScale] = useState(1);

  const computeCalendarScale = useCallback(() => {
    if (!isMobile) return;
    const paperEl = calendarPaperRef.current;
    if (!paperEl) return;

    if (!calendarNaturalSize.current) {
      const rect = paperEl.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return; // not painted yet
      calendarNaturalSize.current = {
        width: rect.width / (calendarScale || 1),
        height: rect.height / (calendarScale || 1),
      };
    }
    const { width: naturalWidth, height: naturalHeight } =
      calendarNaturalSize.current;

    // The app's 9:16 frame IS the viewport for these mobile artifact
    // screens, so this is the real "layout" the calendar must fit
    // inside. visualViewport accounts for mobile browser chrome /
    // on-screen keyboard better than innerWidth/innerHeight.
    const vv = window.visualViewport;
    const viewportWidth = vv ? vv.width : window.innerWidth;
    const viewportHeight = vv ? vv.height : window.innerHeight;

    const MARGIN = 0.92; // small gutter so it never touches screen edges
    const scaleX = (viewportWidth * MARGIN) / naturalWidth;
    const scaleY = (viewportHeight * MARGIN) / naturalHeight;

    // Largest scale that fits BOTH axes — "as large as possible to fit".
    const nextScale = Math.min(scaleX, scaleY);
    setCalendarScale(Math.min(Math.max(nextScale, 1), 2.4));
  }, [isMobile, calendarScale]);

  useEffect(() => {
    if (!isMobile) return;
    window.visualViewport?.addEventListener("resize", computeCalendarScale);
    window.addEventListener("orientationchange", computeCalendarScale);
    return () => {
      window.visualViewport?.removeEventListener(
        "resize",
        computeCalendarScale,
      );
      window.removeEventListener("orientationchange", computeCalendarScale);
    };
  }, [isMobile, computeCalendarScale]);

  const getDayIndex = (dayName) => {
    return weekDays.indexOf(dayName);
  };

  const generateTimeSlots = () => {
    const dayOfWeek = moment(
      verification?.artifactsResponse?.appointmentResponse?.date,
      "MM/DD/YYYY",
    ).day();
    const dayData = verification?.openingHours?.find(
      (d) => getDayIndex(d.day) === dayOfWeek,
    );

    if (dayData && !dayData.isclosed) {
      const openTime = moment(dayData.opens_at, "HH:mm");
      const closeTime = moment(dayData.closes_at, "HH:mm");
      const slots = [];
      while (openTime.isBefore(closeTime)) {
        slots.push(openTime.format("hh:mm A"));
        openTime.add(30, "minutes");
      }
      return slots;
    }
    return [];
  };
  console.log("AppointmentRespons", artifact);
  const getOpeningHours = (day) => {
    const getDay = verification?.openingHours?.find(
      (d) =>
        getDayIndex(d.day) ===
        moment(
          verification?.artifactsResponse?.appointmentResponse?.date,
          "MM/DD/YYYY",
        ).day(),
    );
    return getDay;
  };

  const handleStartDateChange = (
    field,
    value,
    artifactId,
    selectedOptionIndex,
  ) => {
    verification.setArtifactsResponse((prev) => ({
      ...prev,
      appointmentResponse: {
        ...prev?.appointmentResponse,
        [field]: value,
        timezone: userTimezone,
        user_id: parseInt(verification?.customer?.userId),
        appointment_id: artifactId,
        created_by: parseInt(verification?.customer?.userId),
        updated_by: parseInt(verification?.customer?.userId),
        vehicle_id: parseInt(
          verification?.artifactsResponse?.vehicleTransactionResponse
            ?.vehicle_id,
        ),
      },
      appointmentOption: {
        ...prev.appointmentOption,
        index:
          selectedOptionIndex !== undefined
            ? selectedOptionIndex
            : (prev?.appointmentOption?.index ?? ""),
      },
    }));
  };

  useEffect(() => {
    if (!verification?.artifactsResponse?.appointmentResponse) {
      verification.setArtifactsResponse((prev) => ({
        ...prev,
        appointmentResponse: {
          date: "",
          time: "",
          preference: "",
          timezone: userTimezone,
          user_id: parseInt(verification?.customer?.userId),
          created_by: parseInt(verification?.customer?.userId),
          updated_by: parseInt(verification?.customer?.userId),
          type: "appointment",
          dealership_id: parseInt(verification.dealershipId),
          employee_user_id: parseInt(verification.employeeId),
          vehicle_id: parseInt(
            verification?.artifactsResponse?.vehicleTransactionResponse
              ?.vehicle_id,
          ),
        },
        appointmentOption: {
          errorMessage: "",
          index: "",
          dateError: "",
        },
      }));
    }
  }, []);

  // Shared border styling for the outlined inputs (date / time / preference)
  const outlineSx = {
    backgroundColor: isAIAppointment ? "white" : "transparent",
    borderRadius: "6px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: isAIAppointment ? " #00AB55" : Theme.palette.primary.main,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: isAIAppointment ? " #00AB55" : Theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: isAIAppointment ? " #00AB55" : Theme.palette.primary.main,
    },
  };

  // Wider fields on mobile so they are easier to tap
  const fieldWidth = isMobile ? "92%" : "70%";

  const renderAppointmentSelectors = () => (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column", // Always column layout
          gap: isMobile ? "14px" : "16px", // tighter spacing between the three fields
          alignItems: "center", // Center align items
          width: "100%",
          maxWidth: isMobile ? "480px" : "400px", // Wider on mobile
          margin: "0 auto", // Center the entire container
        }}
      >
        {/* Date Selector */}
        <Box
          sx={{
            ...ArtifactStyle.datePicker,
            width: fieldWidth,
            display: "flex",
            flexDirection: "column",
            alignItems: "center", // Center content
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              // label="Select Date"
              // v5 picks Desktop (Popper) vs Mobile (Dialog) based on
              // POINTER TYPE (coarse/touch => Dialog). We keep that native
              // behavior on purpose: a centered Dialog can scale up to fill
              // the 9:16 frame without overflowing, the way an anchored
              // Popper dropdown would.
              value={
                verification?.artifactsResponse?.appointmentResponse?.date
                  ? new Date(
                      verification?.artifactsResponse?.appointmentResponse
                        ?.date,
                    )
                  : null
              }
              minDate={
                verification?.artifactsResponse?.appointmentResponse
                  ? new Date()
                  : null
              }
              onOpen={() => {
                onInteraction?.();
                // Measure/scale after the Dialog has actually painted.
                // Double rAF: first waits for React's commit, second for
                // the browser's layout/paint of that commit.
                requestAnimationFrame(() => {
                  requestAnimationFrame(computeCalendarScale);
                });
              }}
              shouldDisableDate={(date) => {
                const dayOfWeek = date.getDay();
                const dayData = verification?.openingHours.find(
                  (d) => getDayIndex(d.day) === dayOfWeek,
                );
                return dayData?.isclosed;
              }}
              onChange={(e) => {
                onInteraction?.();
                handleStartDateChange(
                  "date",
                  moment.tz(e, userTimezone).format("MM/DD/YYYY"),
                  artifact?.id,
                );

                setAppointmentScreen(true);
              }}
              // Scale the MOBILE DIALOG's Paper (not the Desktop Popper —
              // that's what PaperProps targets, and it's ignored on touch
              // devices) to fill as much of the 9:16 frame as possible.
              // transform:scale multiplies every internal MUI element
              // (day cells, header, row heights) uniformly, sidestepping
              // v5's mix of fixed-px inline styles that per-class
              // overrides can't reliably reach.
              DialogProps={
                isMobile
                  ? {
                      PaperProps: {
                        ref: calendarPaperRef,
                        sx: {
                          transform: `scale(${calendarScale})`,
                          transformOrigin: "center center",
                          transition: "transform 0.15s ease-out",
                          ...calendarColorSx,
                        },
                      },
                    }
                  : undefined
              }
              // Desktop (Popper) variant — same color scheme, no scaling
              // (scaling is a mobile-only "fill the 9:16 frame" concern).
              PaperProps={
                !isMobile
                  ? {
                      sx: calendarColorSx,
                    }
                  : undefined
              }
              renderInput={(params) => (
                <TextField
                  size="small"
                  {...params}
                  fullWidth
                  placeholder="Select Date"
                  error={getOpeningHours()?.isclosed ? true : false}
                  helperText={
                    getOpeningHours()?.isclosed
                      ? `Dealership store was closed today`
                      : ""
                  }
                  sx={{ ...outlineSx, ...touchDateInputSx(isMobile) }}
                />
              )}
            />
          </LocalizationProvider>
        </Box>

        {/* Time Selector */}
        <Box
          sx={{
            width: fieldWidth,
            display: "flex",
            flexDirection: "column",
            alignItems: "center", // Center content
          }}
        >
          <FormControl sx={{ width: "100%" }} fullWidth size="small">
            {/* <InputLabel htmlFor="make-select">Select Time</InputLabel> */}
            <Select
              labelId="make-select"
              placeholder="Select Time"
              id="make-select"
              value={
                verification?.artifactsResponse?.appointmentResponse?.time
                  ? verification?.artifactsResponse?.appointmentResponse?.time
                  : ""
              }
              displayEmpty
              renderValue={(selected) => {
                if (selected === "") {
                  return <span style={{ color: "#9e9e9e" }}>Select Time</span>; // grey placeholder
                }
                return selected;
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: isMobile ? "60vh" : 200,
                  },
                },
                anchorOrigin: isMobile
                  ? {
                      vertical: "top",
                      horizontal: "left",
                    }
                  : {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                transformOrigin: isMobile
                  ? {
                      vertical: "bottom",
                      horizontal: "left",
                    }
                  : "auto",
              }}
              onChange={(e) => {
                onInteraction?.();
                handleStartDateChange("time", e.target.value, artifact?.id);
                setAppointmentScreen(true);
              }}
              sx={{ ...outlineSx, ...touchSelectSx(isMobile) }}
            >
              {generateTimeSlots().map((slot, index) => (
                <MenuItem
                  key={index}
                  value={slot}
                  dense={!isMobile}
                  sx={{
                    border: "1px solid #c0c0c0",
                    padding: isMobile ? 0 : "8px",
                    borderRadius: "4px",
                    justifyContent: "center",
                    alignItems: "center",
                    margin: isMobile ? "6px" : "4px",
                    fontSize: isMobile ? "18px" : "16px",
                    ...(isMobile && { minHeight: "56px" }),
                  }}
                >
                  {slot}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Preference Selector */}
        <Box
          sx={{
            ...ArtifactStyle.preference,
            width: fieldWidth,
            display: "flex",
            flexDirection: "column",
            alignItems: "center", // Center content
          }}
        >
          {verification.aiData.spanish ? (
            <FormControl fullWidth>
              <Select
                sx={{ ...outlineSx, ...touchSelectSx(isMobile) }}
                value={
                  verification?.artifactsResponse?.appointmentOption?.index ??
                  ""
                }
                onChange={(event) => {
                  onInteraction?.();
                  const selectedOptionIndex = event.target.value;
                  const preferenceOptions = JSON.parse(
                    artifact?.es_preference_options || "[]",
                  );
                  const selectedPreference =
                    preferenceOptions[selectedOptionIndex];
                  handleStartDateChange(
                    "preference",
                    selectedPreference,
                    artifact?.id,
                    selectedOptionIndex,
                  );
                }}
                size="small"
                disabled={
                  verification?.artifactsResponse?.appointmentResponse?.date ===
                  ""
                }
                displayEmpty
              >
                <MenuItem value="" sx={touchMenuItemSx(isMobile)}>
                  Select Preference
                </MenuItem>
                {JSON.parse(artifact?.es_preference_options || "[]")?.map(
                  (option, index) => (
                    <MenuItem
                      key={index}
                      value={index}
                      sx={touchMenuItemSx(isMobile)}
                    >
                      {option}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          ) : (
            <FormControl fullWidth>
              <Select
                sx={{ ...outlineSx, ...touchSelectSx(isMobile) }}
                value={
                  verification?.artifactsResponse?.appointmentOption?.index ??
                  ""
                }
                onChange={(event) => {
                  onInteraction?.();
                  const selectedOptionIndex = event.target.value;
                  const preferenceOptions = JSON.parse(
                    artifact?.preference_options || "[]",
                  );
                  const selectedPreference =
                    preferenceOptions[selectedOptionIndex];
                  handleStartDateChange(
                    "preference",
                    selectedPreference,
                    artifact?.id,
                    selectedOptionIndex,
                  );
                }}
                size="small"
                disabled={
                  verification?.artifactsResponse?.appointmentResponse?.date ===
                  ""
                }
                displayEmpty
              >
                <MenuItem value="" sx={touchMenuItemSx(isMobile)}>
                  Select Preference
                </MenuItem>
                {JSON.parse(artifact?.preference_options || "[]")?.map(
                  (option, index) => (
                    <MenuItem
                      key={index}
                      value={index}
                      sx={touchMenuItemSx(isMobile)}
                    >
                      {option}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>
    </>
  );

  const hasText = (() => {
    // prefer Spanish text when aiData.spanish is true
    if (verification?.aiData?.spanish) {
      const es = artifact?.es_message ?? artifact?.es_text ?? "";
      return !!(es && String(es).trim().length > 0);
    }
    const en = artifact?.message ?? artifact?.text ?? "";
    return !!(en && String(en).trim().length > 0);
  })();

  return (
    <Box sx={ArtifactStyle.appointmentBox}>
      {isAIAppointment ? (
        <Box
        // sx={{
        //   backgroundColor: isAIAppointment ? "white" : "transparent",
        //   padding: "16px",
        //   borderRadius: "8px",
        // }}
        >
          {/* for AI appointments: only show selectors when there is text */}
          {hasText ? renderAppointmentSelectors() : null}
        </Box>
      ) : (
        <>
          <TextUnAvailable
            text={artifact.message}
            es_text={artifact.es_message}
          />
          {/* only render selectors if there's text (respects Spanish toggle above) */}
          {hasText && renderAppointmentSelectors()}
        </>
      )}
    </Box>
  );
};

export default AppointmentResponse;